import { makeAutoObservable, reaction, runInAction } from 'mobx'
import { type StripeCardElementChangeEvent, type TokenResult } from '@stripe/stripe-js'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { logger } from 'shared/lib'
import { type ZIndex } from 'shared/constants/zIndex'
import { type Source } from 'entities/Billing/model/Source'
import { AddPaymentMethodContent } from 'entities/Billing/ui/AddPaymentMethodModal/ui/AddPaymentMethodContent'
import type { BillingStore } from 'entities/Billing'

export class AddPaymentMethodStore {
  private _modalId = 'add_payment_method_id'
  private _name = ''
  private _isPrimary = false

  private _loading = false

  private _nameError = ''
  private _cardError = ''
  private _apiError = false
  private _cardReady = false

  private _disposeReaction?: () => void

  constructor(
    private _onAddPaymentMethod: (source: Source) => void,
    private _billingStore: BillingStore,
    zIndex?: ZIndex
  ) {
    makeAutoObservable(this)
    this._openModal(zIndex)
  }

  private _closeModal = () => {
    this._disposeReaction?.()
    modalStore.removeModal(this._modalId)
  }

  private _openModal = (zIndex?: ZIndex) => {
    modalStore.addModal({
      id: this._modalId,
      title: 'Add card',
      ModalContent: AddPaymentMethodContent,
      ModalContentProps: {
        addPaymentMethodStore: this,
      },
      primaryAction: {
        text: 'Add card',
        onAction: this._onAddCard,
        disabled: this._loading,
      },
      width: 480,
      zIndex: zIndex,
      disabledOverlayClose: true,
    })

    this._disposeReaction?.()
    this._disposeReaction = reaction(
      () => this._loading,
      (isLoading) => {
        const modal = modalStore.getModal(this._modalId)
        if (modal && 'primaryAction' in modal && modal.primaryAction) {
          modal.primaryAction.disabled = isLoading
        }
      }
    )
  }

  setName = (value: string) => {
    this._name = value
  }

  setIsPrimary = (value: boolean) => {
    this._isPrimary = value
  }

  onChangeCard = (event: StripeCardElementChangeEvent) => {
    this._cardReady = event.complete
    this._cardError = event.error?.message || ''
  }

  private _onSubmitCard: (() => Promise<TokenResult | undefined>) | null = null

  setOnSubmit = (onSubmit: () => Promise<TokenResult | undefined>) => {
    this._onSubmitCard = onSubmit
  }

  clearCardError = () => {
    this._cardError = ''
  }

  clearApiError = () => {
    this._apiError = false
  }

  private _checkError = () => {
    this._nameError = ''
    this._cardError = ''
    if (!this.name.trim().length) {
      this._nameError = 'Cardholder name is required'
    } else {
      this._nameError = ''
    }
    if (!this._cardReady && !this._cardError) {
      this._cardError = 'Credit card is required'
    } else if (this._cardReady) {
      this._cardError = ''
    }
    return !!this._nameError || !!this._cardError
  }
  private _onAddCard = async () => {
    if (this._loading) return
    this._apiError = false
    const isError = this._checkError()
    if (isError) return
    this._loading = true
    if (this._onSubmitCard) {
      const res = await this._onSubmitCard()
      if (!res || res?.error) {
        runInAction(() => {
          this._cardError = res?.error.message || 'Please provide another credit card'
          this._loading = false
        })
        return
      }
      try {
        const source = await this._billingStore.createSource({
          card_token: res.token.id,
          name: this._name,
          backup: false,
          primary: this._isPrimary,
          type: 'default',
          confirm: false,
          email: '',
        })
        if (source) {
          await this._billingStore.fetchBillingSources()
          this._onAddPaymentMethod(source)
          this._closeModal()
        }
      } catch (e) {
        runInAction(() => {
          this._apiError = true
        })
        logger.error(e)
      }
    }
    runInAction(() => {
      this._loading = false
    })
  }

  get name() {
    return this._name
  }

  get isPrimary() {
    return this._isPrimary
  }

  get nameError() {
    return this._nameError
  }

  get cardError() {
    return this._cardError
  }

  get apiError() {
    return this._apiError
  }
}
